package horcrux.frontend.common

import arrow.core.Either
import arrow.core.Option
import arrow.core.getOrElse
import arrow.core.raise.Raise
import arrow.core.raise.either
import arrow.resilience.Schedule
import arrow.resilience.ScheduleStep
import horcrux.common.HorcruxEither
import horcrux.common.HorcruxRequestSimpleError
import horcrux.common.ValueClass
import horcrux.common.api.dtos.types.DocumentMetadata
import horcrux.common.api.dtos.types.DocumentName
import horcrux.common.utils.AppLogger
import horcrux.common.utils.Validatable
import horcrux.common.utils.getOrThrow
import horcrux.frontend.common.configs.StaticContentConfigs
import horcrux.frontend.common.views.components.containers.UpdateBanner
import io.ktor.client.plugins.ResponseException
import io.ktor.util.logging.KtorSimpleLogger
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.delay
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch
import mui.material.AlertColor
import mui.material.ButtonProps
import mui.system.PropsWithSx
import mui.system.sx
import react.EffectBuilder
import react.Props
import react.StateInstance
import react.dom.events.FormEvent
import react.router.NavigateFunction
import react.router.useNavigate
import react.useEffect
import web.cssom.Font
import web.cssom.FontFamily
import web.cssom.FontSize
import web.cssom.FontStyle
import web.cssom.TextAlign
import web.html.HTMLDivElement
import kotlin.time.Duration.Companion.ZERO

private val logger = KtorSimpleLogger("horcrux.frontend.Utils")

fun <T> StateInstance<T>.get(): T =
    this.component1()

fun <T> StateInstance<T>.set(value: T) =
    this.component2()(value)

fun FormEvent<HTMLDivElement>.value(): String = this.target.asDynamic().value as String

class HandledException(causeMaybe: Throwable? = null) : HorcruxClientError(causeMaybe)

fun <A> HorcruxEither<A>.handleErrors(block: (HorcruxRequestSimpleError) -> Option<Throwable>): HorcruxEither<A> =
    this.mapLeft { cause ->
        when (cause) {
            is HorcruxRequestSimpleError -> block(cause).getOrElse { HandledException(cause) }
            else -> cause
        }
    }

fun <A> HorcruxEither<A>.handleResponseException(block: (ResponseException) -> Option<Throwable>): HorcruxEither<A> =
    this.mapLeft { cause ->
        when (cause) {
            is ResponseException -> block(cause).getOrElse { HandledException(cause) }
            else -> cause
        }
    }

fun <A : Validatable<A>> StateInstance<A?>.isInvalid(): Boolean =
    this.get()?.let { it.validate().isLeft() } ?: false

fun <A : Validatable<A>> State<A?>.isInvalid(): Boolean =
    this.get()?.let { it.validate().isLeft() } ?: false

fun <A> StateInstance<A?>.clear() = this.set(null)

fun <A : ValueClass<String, A>> StateInstance<A?>.getStringValueOrEmpty(): String = this.get()?.value ?: ""

fun <A : ValueClass<String, A>> State<A?>.getStringValueOrEmpty(): String = this.get()?.value ?: ""

fun ButtonProps.navigateToOnClick(route: Route) {
    val navigate = useNavigate()
    this.href = route.withHash
    this.onClick = { event ->
        event.preventDefault()
        navigate(route.routeName)
    }
}

fun PropsWithSx.withDefaultCSS() {
    this.sx {
        this.textAlign = TextAlign.center
        this.font = Font(FontStyle.normal, FontSize.medium, FontFamily.monospace)
        this.color = StaticContentConfigs.GRAY
    }
}

fun ShowDialogState.open() = this.set(true)
typealias ShowDialogState = StateInstance<Boolean>

fun ShowDialogState.close() = this.set(false)
fun List<DocumentMetadata>.getDocumentNames(): Set<DocumentName> =
    this.mapNotNull { it.documentName }.toSet()

fun State<DocumentName?>.isInvalidOrNotUnique(documentNames: Set<DocumentName>): Boolean =
    this.isInvalid() || this.get()?.let { documentNames.contains(it) } ?: false

fun UpdateBanner.displaySignOutMessage() {
    this(AlertColor.info, "You have been signed out")
}

class RetryPolicy<Result>(
    private val retrySchedule: Schedule<Throwable, *>,
    private val shouldRetry: (Throwable) -> Boolean
) : AppLogger {
    /**
     * Retries action according to schedule until either schedule is exhausted, an error that shouldRetry maps to false is encountered, or action is successful
     */
    suspend fun retry(
        action: suspend () -> Result,
    ): Either<Throwable, Result> {
        var step: ScheduleStep<Throwable, *> = retrySchedule.step
        while (true) {
            currentCoroutineContext().ensureActive()
            when (val result = Either.catch { action.invoke() }) {
                is Either.Left -> if (shouldRetry(result.value)) {
                    when (val decision = step(result.value)) {
                        is Schedule.Decision.Continue -> {
                            logger.debug("Received error, retrying.", result.value)
                            if (decision.delay != ZERO) delay(decision.delay)
                            step = decision.step
                        }

                        is Schedule.Decision.Done -> return result
                    }
                } else {
                    return result
                }

                is Either.Right -> return result
            }
        }
    }
}

fun <Error : Throwable> io(
    retryPolicyMaybe: RetryPolicy<Unit>? = null,
    mapError: (Error) -> Unit = { _ -> },
    block: suspend Raise<Error>.() -> Unit
) = GlobalScope.launch {
    retryPolicyMaybe?.let { it.retry { either { block() }.onLeft(mapError).getOrThrow() } }
        ?: either { block() }.onLeft(mapError).getOrThrow()
}

fun <K, V> Map<K, V>.replace(key: K, value: V): Map<K, V> = this.filter { it.key != key } + mapOf(key to value)

val Props.navigate: NavigateFunction
    get() = useNavigate()

fun isBrowserEnvironment(): Boolean =
    Either.catch { js("![typeof window, typeof document].includes('undefined')") as Boolean }.fold({ false }, { it })

fun useEffectAsync(
    vararg dependencies: Any?,
    effect: suspend EffectBuilder.() -> Unit,
) {
    useEffect(*dependencies) {
        GlobalScope.launch { effect() }
    }
}
